/**
 * Task hand-off rules for the assignee control (LXP-55).
 *
 * The Team role only sees tasks it's assigned to, so handing a task off
 * entirely would make it vanish from the giver's own list. Two rules keep that
 * from happening by accident:
 *
 *   1. **Hand-off keeps the giver on.** Adding a new Lead while you hold the
 *      Lead seat moves you to Contributor rather than leaving two Leads or
 *      dropping you.
 *   2. **Removing yourself is confirmed** when it is your last seat on the task
 *      and your role is assignment-scoped — the task leaves your list.
 *
 * Pure so it can be unit-tested; `.tsx` files are not collected by vitest.
 */

export interface AssignmentSeat {
  id: string
  employeeId: string
  role: string
}

export type HandoffStep =
  /** PATCH the giver's lead seat to contributor. */
  | { kind: 'demote'; assignmentId: string }
  /** The giver already holds a contributor seat — just drop the lead seat. */
  | { kind: 'drop'; assignmentId: string }

/**
 * What to do with the current user's own seat when they add `newRole` for
 * `newEmployeeId` on a task. Null when nothing changes.
 */
export function handoffStep(
  seats: AssignmentSeat[],
  input: { currentEmployeeId: string | null | undefined; newEmployeeId: string; newRole: string },
): HandoffStep | null {
  const { currentEmployeeId, newEmployeeId, newRole } = input
  if (!currentEmployeeId || newRole !== 'lead' || newEmployeeId === currentEmployeeId) return null

  const mine = seats.filter((s) => s.employeeId === currentEmployeeId)
  const myLead = mine.find((s) => s.role === 'lead')
  if (!myLead) return null

  // The (employee, entity, role) triple is unique, so demoting into a seat the
  // giver already holds would collide — drop the lead seat instead.
  const alreadyContributor = mine.some((s) => s.role === 'contributor')
  return alreadyContributor
    ? { kind: 'drop', assignmentId: myLead.id }
    : { kind: 'demote', assignmentId: myLead.id }
}

/**
 * Whether removing `seat` would drop the current user off the task entirely —
 * worth a confirm for an assignment-scoped role, because the task leaves
 * their list and only an admin or a co-assignee can put them back.
 */
export function removalHidesTask(
  seats: AssignmentSeat[],
  seat: AssignmentSeat,
  input: { currentEmployeeId: string | null | undefined; scoped: boolean },
): boolean {
  if (!input.scoped || !input.currentEmployeeId) return false
  if (seat.employeeId !== input.currentEmployeeId) return false
  return !seats.some((s) => s.id !== seat.id && s.employeeId === input.currentEmployeeId)
}
